"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { logger } from "@/core/logging";
import { sessionStorageService } from "@/core/storage";
export type PixelMatchState = {
  /** Data-URL of the diff image, or null while computing / on error / not yet started. */
  diffImageUrl: string | null;
  /** True while the async computation is running. */
  isProcessing: boolean;
};
type ComputeOptions = {
  /**
   * pixelmatch threshold (0–1). Lower = more sensitive.
   * Prototype uses 0.05 (moderate sensitivity).
   */
  threshold?: number;
};
type UsePixelMatchOptions = {
  /**
   * Optional storage scope used to retain only the latest diff entry for a
   * given view. Example: `front` and `profile` can each keep one cached diff
   * without evicting each other.
   */
  storageScope?: string;
  persistence?: PixelMatchPersistence;
};
export type PixelMatchPersistence = {
  readValue: (key: string) => string | null;
  writeValue: (key: string, base64: string) => void;
  removeValue: (key: string) => void;
  readScopedActiveKey: (scope: string) => string | null;
  writeScopedActiveKey: (scope: string, key: string) => void;
  removeScopedActiveKey: (scope: string) => void;
};

/**
 * Strip signed-URL query parameters so that AWS/CloudFront token rotation
 * doesn't bust the cache key when the underlying image is the same.
 * Removes X-Amz-*, Policy, Signature, Key-Pair-Id, and generic expiry params.
 */
function stripSignatureParams(url: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  const SIGNED_PARAMS = /^(X-Amz-|Policy$|Signature$|Key-Pair-Id$|Expires$|token$)/i;
  for (const key of [...parsed.searchParams.keys()]) {
    if (SIGNED_PARAMS.test(key)) parsed.searchParams.delete(key);
  }
  return parsed.toString();
}
function isSupportedComputeDiffUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// sessionStorage persistence helpers
//
// Diff PNGs are stored as base64 strings keyed by the normalised URL pair.
// This lets the component restore the diff immediately on a full page reload
// without calling the API again — mirroring how the browser HTTP-caches the
// underlying images.
//
// Chunk size keeps each String.fromCharCode spread call well below V8's
// maximum argument count.
// ---------------------------------------------------------------------------

const SESSION_KEY_PREFIX = "pixel_match_v1_";
/** Soft cap: skip caching diffs larger than 2 MB (very unusual for a sparse overlay). */
const SESSION_MAX_BYTES = 2 * 1024 * 1024;
const BTOA_CHUNK = 0x8000;
function buildSessionValueKey(key: string): string {
  return `${SESSION_KEY_PREFIX}${key}`;
}
function buildScopeSlotKey(scope: string): string {
  return buildSessionValueKey(`scope_${scope}_slot`);
}
const sessionPixelMatchPersistence: PixelMatchPersistence = {
  readValue: key => sessionStorageService.getItemSync<string>(buildSessionValueKey(key)),
  writeValue: (key, base64) => sessionStorageService.setItemSync(buildSessionValueKey(key), base64),
  removeValue: key => sessionStorageService.removeItemSync(buildSessionValueKey(key)),
  readScopedActiveKey: scope => sessionStorageService.getItemSync<string>(buildScopeSlotKey(scope)),
  writeScopedActiveKey: (scope, key) => sessionStorageService.setItemSync(buildScopeSlotKey(scope), key),
  removeScopedActiveKey: scope => sessionStorageService.removeItemSync(buildScopeSlotKey(scope))
};
function readPersistentValue(persistence: PixelMatchPersistence, key: string): string | null {
  try {
    return persistence.readValue(key);
  } catch {
    return null;
  }
}
function writePersistentValue(persistence: PixelMatchPersistence, key: string, base64: string): void {
  try {
    // Rough byte estimate: base64 len × 0.75.
    if (base64.length * 0.75 > SESSION_MAX_BYTES) return;
    persistence.writeValue(key, base64);
  } catch {
    // QuotaExceededError or SecurityError — silently skip.
  }
}
function removePersistentValue(persistence: PixelMatchPersistence, key: string): void {
  try {
    persistence.removeValue(key);
  } catch {
    // Ignore.
  }
}
function readScopedActiveKey(persistence: PixelMatchPersistence, scope: string | undefined): string | null {
  if (!scope) return null;
  try {
    return persistence.readScopedActiveKey(scope);
  } catch {
    return null;
  }
}
function writeScopedActiveKey(persistence: PixelMatchPersistence, scope: string | undefined, key: string): void {
  if (!scope) return;
  try {
    persistence.writeScopedActiveKey(scope, key);
  } catch {
    // Ignore quota/security errors.
  }
}
function removeScopedActiveKey(persistence: PixelMatchPersistence, scope: string | undefined): void {
  if (!scope) return;
  try {
    persistence.removeScopedActiveKey(scope);
  } catch {
    // Ignore.
  }
}

/** Encode an ArrayBuffer to a base64 string without blowing the call stack. */
function encodeArrayBufferToBase64(ab: ArrayBuffer): string {
  const bytes = new Uint8Array(ab);
  const chunks: string[] = [];
  for (let i = 0; i < bytes.length; i += BTOA_CHUNK) {
    chunks.push(String.fromCharCode(...bytes.subarray(i, i + BTOA_CHUNK)));
  }
  return btoa(chunks.join(""));
}
function buildDiffDataUrl(base64: string): string {
  return `data:image/png;base64,${base64}`;
}
function readBlobWithFileReader(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read blob"));
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Unexpected file reader result"));
        return;
      }
      resolve(result);
    };
    reader.readAsDataURL(blob);
  });
}
async function readBlobAsBase64(blob: Blob): Promise<string> {
  if (typeof blob.arrayBuffer === "function") {
    return encodeArrayBufferToBase64(await blob.arrayBuffer());
  }
  const dataUrl = await readBlobWithFileReader(blob);
  const commaIndex = dataUrl.indexOf(",");
  return commaIndex === -1 ? dataUrl : dataUrl.slice(commaIndex + 1);
}

/** Status codes that are worth retrying (transient server/gateway errors). */
const RETRYABLE_STATUSES = new Set([429, 502, 503, 504]);
const MAX_RETRIES = 2;

/**
 * Computes a pixel-level diff between two image URLs by delegating to the
 * server-side POST /api/compute-diff endpoint.
 *
 * The server fetches both images from S3/CDN, runs pixelmatch, and returns a
 * sparse overlay PNG (transparent where unchanged, semi-transparent
 * magenta-purple where changed). Raw image pixels never transit the browser
 * wire and the browser never calls getImageData() on cross-origin content.
 *
 * Race condition safety: each call is tagged with a generation counter. If a
 * newer call starts (or clearCache is called) while a computation is in
 * flight, the stale result is silently discarded — state is never clobbered.
 *
 * Cancellation: each computeDiff call creates an AbortController and passes
 * its signal to fetch. Starting a new call or calling clearCache aborts the
 * previous in-flight request, freeing server-side resources immediately
 * rather than waiting for the response to complete.
 *
 * Cache key: signed URL query params (X-Amz-*, Policy, etc.) are stripped so
 * that CDN token rotation doesn't cause unnecessary recomputation.
 *
 * Reload persistence: on a successful diff, the PNG is stored in
 * sessionStorage (base64-encoded, keyed by the stripped URL pair). On the
 * next mount — including a full page reload — the stored diff is restored
 * immediately without calling the API, matching the behaviour of the browser
 * HTTP cache for the underlying images.
 *
 * Retry: transient gateway errors (502, 503, 504) are retried up to twice
 * with 1 s → 2 s exponential backoff. Client errors (4xx) are not retried.
 *
 * Persistence format: the UI renders a stable data URL instead of a revocable
 * blob URL, so reloads and long-lived sessions do not lose the diff image just
 * because an object URL was invalidated.
 */
export function usePixelMatch(options: UsePixelMatchOptions = {}) {
  const {
    persistence = sessionPixelMatchPersistence,
    storageScope
  } = options;
  const [state, setState] = useState<PixelMatchState>({
    diffImageUrl: null,
    isProcessing: false
  });
  const cacheRef = useRef<{
    key: string;
    diffImageUrl: string | null;
  } | null>(null);
  const computeGenRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  // Proactively restore the most recent completed diff for this scope from
  // sessionStorage before computeDiff is called.
  useEffect(() => {
    if (!storageScope) return;
    const activeKey = readScopedActiveKey(persistence, storageScope);
    if (!activeKey) {
      cacheRef.current = null;
      setState(prev => prev.diffImageUrl === null && !prev.isProcessing ? prev : {
        diffImageUrl: null,
        isProcessing: false
      });
      return;
    }
    const stored = readPersistentValue(persistence, activeKey);
    if (!stored) {
      cacheRef.current = null;
      setState(prev_0 => prev_0.diffImageUrl === null && !prev_0.isProcessing ? prev_0 : {
        diffImageUrl: null,
        isProcessing: false
      });
      return;
    }
    const diffImageUrl = buildDiffDataUrl(stored);
    cacheRef.current = {
      key: activeKey,
      diffImageUrl
    };
    setState({
      diffImageUrl,
      isProcessing: false
    });
  }, [persistence, storageScope]);

  // Cancel any in-flight fetch on unmount.
  useEffect(() => {
    return () => {
      computeGenRef.current += 1;
      abortRef.current?.abort();
    };
  }, []);
  const clearCachedDiff = useCallback(() => {
    if (cacheRef.current?.key) {
      removePersistentValue(persistence, cacheRef.current.key);
      if (readScopedActiveKey(persistence, storageScope) === cacheRef.current.key) {
        removeScopedActiveKey(persistence, storageScope);
      }
    }
    cacheRef.current = null;
  }, [persistence, storageScope]);
  const computeDiff = useCallback(async (beforeUrl: string, afterUrl: string, options_0: ComputeOptions = {}): Promise<PixelMatchState> => {
    const {
      threshold = 0.05
    } = options_0;

    // The server endpoint only accepts remote http(s) image URLs. During
    // retakes we may temporarily have local data URLs; skip diffing until
    // the assessment has durable URLs again and clear any stale overlay.
    if (!isSupportedComputeDiffUrl(beforeUrl) || !isSupportedComputeDiffUrl(afterUrl)) {
      computeGenRef.current += 1;
      abortRef.current?.abort();
      clearCachedDiff();
      const nextState: PixelMatchState = {
        diffImageUrl: null,
        isProcessing: false
      };
      setState(nextState);
      return nextState;
    }

    // Cache by signature-stripped URLs so CDN token rotation doesn't retrigger.
    const cacheKey = `${stripSignatureParams(beforeUrl)}||${stripSignatureParams(afterUrl)}||${threshold}`;

    // In-memory cache hit (same component lifecycle).
    if (cacheRef.current?.key === cacheKey) {
      return {
        diffImageUrl: cacheRef.current.diffImageUrl,
        isProcessing: false
      };
    }

    // sessionStorage hit — restores the diff across full page reloads without
    // calling the API, matching how the browser HTTP-caches the source images.
    const stored_0 = readPersistentValue(persistence, cacheKey);
    if (stored_0) {
      const diffImageUrl_0 = buildDiffDataUrl(stored_0);
      cacheRef.current = {
        key: cacheKey,
        diffImageUrl: diffImageUrl_0
      };
      const restoredState: PixelMatchState = {
        diffImageUrl: diffImageUrl_0,
        isProcessing: false
      };
      setState(restoredState);
      return restoredState;
    }

    // Cancel any in-flight request before starting a new one.
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    const gen = ++computeGenRef.current;
    setState(prev_1 => ({
      ...prev_1,
      isProcessing: true
    }));
    let lastStatus: number | null = null;
    let lastError = "";
    let attemptsMade = 0;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      attemptsMade = attempt + 1;
      if (gen !== computeGenRef.current) {
        return {
          diffImageUrl: null,
          isProcessing: false
        };
      }

      // Exponential backoff before retry attempts.
      if (attempt > 0) {
        await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        if (gen !== computeGenRef.current) {
          return {
            diffImageUrl: null,
            isProcessing: false
          };
        }
      }
      try {
        const response = await fetch("/api/compute-diff", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            beforeUrl,
            afterUrl,
            threshold
          }),
          signal: ac.signal
        });
        if (gen !== computeGenRef.current) {
          return {
            diffImageUrl: null,
            isProcessing: false
          };
        }
        if (!response.ok) {
          lastStatus = response.status;
          // Try to extract a meaningful error message from the response body.
          let serverMessage = "";
          try {
            const json = (await response.json()) as {
              error?: string;
            };
            serverMessage = json.error ?? "";
          } catch {
            // Ignore JSON parse failure; raw status is enough.
          }
          lastError = serverMessage || `HTTP ${response.status}`;
          if (RETRYABLE_STATUSES.has(response.status) && attempt < MAX_RETRIES) {
            continue;
          }
          throw new Error(lastError);
        }
        const blob = await response.blob();
        if (gen !== computeGenRef.current) {
          return {
            diffImageUrl: null,
            isProcessing: false
          };
        }
        const base64 = await readBlobAsBase64(blob);
        const diffImageUrl_1 = buildDiffDataUrl(base64);
        cacheRef.current = {
          key: cacheKey,
          diffImageUrl: diffImageUrl_1
        };

        // Persist to sessionStorage so a full page reload can restore the diff
        // without calling the API again.
        const previousScopedKey = readScopedActiveKey(persistence, storageScope);
        if (previousScopedKey && previousScopedKey !== cacheKey) {
          removePersistentValue(persistence, previousScopedKey);
        }
        writePersistentValue(persistence, cacheKey, base64);
        writeScopedActiveKey(persistence, storageScope, cacheKey);
        const nextState_0: PixelMatchState = {
          diffImageUrl: diffImageUrl_1,
          isProcessing: false
        };
        setState(nextState_0);
        return nextState_0;
      } catch (err) {
        // A stale gen or AbortError means a newer call cancelled us — discard.
        if (gen !== computeGenRef.current) {
          return {
            diffImageUrl: null,
            isProcessing: false
          };
        }
        const isAbort = err instanceof Error && err.name === "AbortError" || err instanceof DOMException && err.name === "AbortError";
        if (isAbort) {
          return {
            diffImageUrl: null,
            isProcessing: false
          };
        }
        lastError = err instanceof Error ? err.message : String(err);

        // Retry on network-level failures (TypeError from fetch) too.
        const isNetworkError = err instanceof TypeError;
        if (isNetworkError && attempt < MAX_RETRIES) {
          continue;
        }

        // Exhausted retries — fall through to failure handling below.
        break;
      }
    }

    // All attempts failed.
    logger.warn("pixel_match_diff_failed", {
      error: lastError,
      status: lastStatus,
      attempts: attemptsMade
    });
    // Don't cache permanent failures — let a future attempt retry if the
    // caller re-triggers (e.g. after a URL refresh).
    const nextState_1: PixelMatchState = {
      diffImageUrl: null,
      isProcessing: false
    };
    setState(nextState_1);
    return nextState_1;
  }, [clearCachedDiff, persistence, storageScope]);
  const clearCache = useCallback(() => {
    // Increment generation counter to cancel any in-flight computeDiff call.
    computeGenRef.current += 1;
    abortRef.current?.abort();

    // Remove the sessionStorage entry so the next computeDiff recomputes fresh.
    clearCachedDiff();
    setState({
      diffImageUrl: null,
      isProcessing: false
    });
  }, [clearCachedDiff]);
  return {
    ...state,
    computeDiff,
    clearCache
  };
}